import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import * as d3 from 'd3';

const SPARKLINE_WIDTH = 150;
const SPARKLINE_HEIGHT = 40;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '8px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
  },
  sortWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#6b7280',
  },
  select: {
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    padding: '4px 8px',
    fontSize: '12px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: '#374151',
    background: '#fff',
    cursor: 'pointer',
    outline: 'none',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '8px',
  },
  cell: {
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px',
    background: '#fff',
    cursor: 'pointer',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  cellHover: {
    borderColor: '#2563eb',
    boxShadow: '0 1px 4px rgba(37,99,235,0.15)',
  },
  label: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#374151',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginBottom: '4px',
  },
  bottomRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '4px',
  },
  price: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#1f2937',
  },
  changePositive: {
    fontSize: '10px',
    fontWeight: 500,
    color: '#16a34a',
  },
  changeNegative: {
    fontSize: '10px',
    fontWeight: 500,
    color: '#dc2626',
  },
  changeNeutral: {
    fontSize: '10px',
    fontWeight: 500,
    color: '#9ca3af',
  },
  empty: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
    fontSize: '14px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '240px',
  },
  tooltipLabel: {
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    color: '#6b7280',
    fontSize: '11px',
    lineHeight: '1.6',
  },
};

const SORT_OPTIONS = [
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'currentPrice', label: 'Current Price' },
  { value: 'changePercent', label: 'Change %' },
  { value: 'volatility', label: 'Most Volatile' },
];

function computeVolatility(values) {
  if (!values || values.length < 2) return 0;
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  if (mean === 0) return 0;
  const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance) / mean;
}

export default function SmallMultiples({ data }) {
  const svgRefs = useRef({});
  const [sortBy, setSortBy] = useState('city');
  const [hoveredId, setHoveredId] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const enriched = data.map((item) => ({
      ...item,
      _city: (item.label || '').split(',')[0].trim().toLowerCase(),
      _state: (item.label || '').split(',')[1]?.trim().toLowerCase() || '',
      _volatility: computeVolatility(item.values),
    }));

    const sorted = [...enriched];
    sorted.sort((a, b) => {
      switch (sortBy) {
        case 'city':
          return a._city.localeCompare(b._city);
        case 'state': {
          const sc = a._state.localeCompare(b._state);
          return sc !== 0 ? sc : a._city.localeCompare(b._city);
        }
        case 'currentPrice': {
          const ap = a.currentPrice ?? -Infinity;
          const bp = b.currentPrice ?? -Infinity;
          return bp - ap;
        }
        case 'changePercent': {
          const ac = Math.abs(a.changePercent ?? 0);
          const bc = Math.abs(b.changePercent ?? 0);
          return bc - ac;
        }
        case 'volatility':
          return b._volatility - a._volatility;
        default:
          return 0;
      }
    });
    return sorted;
  }, [data, sortBy]);

  // Draw all SVG sparklines in a single useEffect
  useEffect(() => {
    if (!sortedData || sortedData.length === 0) return;

    sortedData.forEach((item) => {
      const svgEl = svgRefs.current[item.addressId];
      if (!svgEl) return;

      const svg = d3.select(svgEl);
      svg.selectAll('*').remove();

      const values = item.values;
      if (!values || values.length < 2) {
        // Draw a dashed horizontal line for insufficient data
        svg
          .append('line')
          .attr('x1', 0)
          .attr('y1', SPARKLINE_HEIGHT / 2)
          .attr('x2', SPARKLINE_WIDTH)
          .attr('y2', SPARKLINE_HEIGHT / 2)
          .attr('stroke', '#d1d5db')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '2 2');
        return;
      }

      const padding = 0.05;
      const minVal = d3.min(values);
      const maxVal = d3.max(values);
      const range = maxVal - minVal || 1;
      const yPad = range * padding;

      const xScale = d3
        .scaleLinear()
        .domain([0, values.length - 1])
        .range([0, SPARKLINE_WIDTH]);

      const yScale = d3
        .scaleLinear()
        .domain([minVal - yPad, maxVal + yPad])
        .range([SPARKLINE_HEIGHT, 0]);

      const lineColor =
        item.changePercent != null && item.changePercent < 0 ? '#dc2626' : '#2563eb';

      // Area fill
      const area = d3
        .area()
        .x((d, i) => xScale(i))
        .y0(SPARKLINE_HEIGHT)
        .y1((d, i) => yScale(d));

      svg
        .append('path')
        .datum(values)
        .attr('d', area)
        .attr('fill', lineColor)
        .attr('fill-opacity', 0.1);

      // Line
      const line = d3
        .line()
        .x((d, i) => xScale(i))
        .y((d) => yScale(d));

      svg
        .append('path')
        .datum(values)
        .attr('d', line)
        .attr('fill', 'none')
        .attr('stroke', lineColor)
        .attr('stroke-width', 1.5)
        .attr('stroke-linejoin', 'round')
        .attr('stroke-linecap', 'round');
    });
  }, [sortedData]);

  const handleMouseEnter = useCallback(
    (e, item) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const values = item.values || [];
      const dates = item.dates || [];

      const minPrice = values.length > 0 ? Math.min(...values) : null;
      const maxPrice = values.length > 0 ? Math.max(...values) : null;
      const dateRange =
        dates.length >= 2 ? `${dates[0]} to ${dates[dates.length - 1]}` : dates[0] || 'N/A';

      setHoveredId(item.addressId);
      setTooltip({
        left: rect.left + rect.width / 2,
        top: rect.top - 4,
        label: item.label,
        dateRange,
        minPrice,
        maxPrice,
        currentPrice: item.currentPrice,
      });
    },
    []
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredId(null);
    setTooltip(null);
  }, []);

  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Trajectories — All Addresses</div>
        <div style={styles.empty}>No trajectory data available</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Price Trajectories — All Addresses</div>
          <div style={styles.subtitle}>
            Each mini-chart shows pest365 pricing over time
          </div>
        </div>
        <div style={styles.sortWrapper}>
          <span>Sort by:</span>
          <select
            style={styles.select}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={styles.grid}>
        {sortedData.map((item) => {
          const isHovered = hoveredId === item.addressId;
          const changeStyle =
            item.changePercent != null && item.changePercent > 0
              ? styles.changePositive
              : item.changePercent != null && item.changePercent < 0
              ? styles.changeNegative
              : styles.changeNeutral;

          let changeText = '--';
          if (item.changePercent != null) {
            const prefix = item.changePercent > 0 ? '+' : '';
            changeText = `${prefix}${item.changePercent.toFixed(1)}%`;
          }

          return (
            <div
              key={item.addressId}
              style={{
                ...styles.cell,
                ...(isHovered ? styles.cellHover : {}),
              }}
              onMouseEnter={(e) => handleMouseEnter(e, item)}
              onMouseLeave={handleMouseLeave}
            >
              <div style={styles.label} title={item.label}>
                {item.label}
              </div>
              <svg
                ref={(el) => {
                  if (el) svgRefs.current[item.addressId] = el;
                }}
                width="100%"
                height={SPARKLINE_HEIGHT}
                viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}
                preserveAspectRatio="none"
              />
              <div style={styles.bottomRow}>
                <span style={styles.price}>
                  {item.currentPrice != null
                    ? `$${item.currentPrice.toFixed(2)}`
                    : '--'}
                </span>
                <span style={changeStyle}>{changeText}</span>
              </div>
            </div>
          );
        })}
      </div>

      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.left,
            top: tooltip.top,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div style={styles.tooltipLabel}>{tooltip.label}</div>
          <div style={styles.tooltipRow}>
            <span>Date range:</span>
            <span style={{ color: '#374151' }}>{tooltip.dateRange}</span>
          </div>
          <div style={styles.tooltipRow}>
            <span>Min:</span>
            <span style={{ color: '#374151' }}>
              {tooltip.minPrice != null ? `$${tooltip.minPrice.toFixed(2)}` : '--'}
            </span>
          </div>
          <div style={styles.tooltipRow}>
            <span>Max:</span>
            <span style={{ color: '#374151' }}>
              {tooltip.maxPrice != null ? `$${tooltip.maxPrice.toFixed(2)}` : '--'}
            </span>
          </div>
          <div style={styles.tooltipRow}>
            <span>Current:</span>
            <span style={{ color: '#374151', fontWeight: 600 }}>
              {tooltip.currentPrice != null
                ? `$${tooltip.currentPrice.toFixed(2)}`
                : '--'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
